// ─────────────────────────────────────────────────────────────────────────────
// ContactEditor.tsx — ContentSectionEditPageProduct
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useRef } from 'react';
import ApiPost from '../../../../../../api-controller-mod/api-serv/post-api/postApi';
import SelectFieldInline from '../../../../../../component-controller-mod/component-serv/input-fs-cp/select-field-op/ts/selectFieldInline';
import { ContactSection, ContactListItem, MediaSaveCallback } from './types';
import { LabelRow, SplitRow, DraftTextField, DraftTextArea, ImageUploadField } from './components';

const ContactEditor: React.FC<{
  data:               ContactSection;
  onChange:           (v: ContactSection) => void;
  onMediaClick?:      (onSave: MediaSaveCallback) => void;
  userId:             string;
  registerMediaUsage: (mediaUuid: string) => void;
}> = ({ data, onChange, onMediaClick, userId, registerMediaUsage }) => {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [cardEdits,   setCardEdits]   = useState<Record<number, Partial<ContactListItem>>>({});

  const orig     = useRef(data);
  const origCard = useRef<ContactListItem | undefined>(data.lists[0]);

  useEffect(() => {
    origCard.current = orig.current.lists[selectedIdx];
  }, [selectedIdx]);

  const set = (p: Partial<ContactSection>) => onChange({ ...data, ...p });

  const card       = data.lists[selectedIdx];
  const editedCard = card ? { ...card, ...(cardEdits[selectedIdx] ?? {}) } : undefined;

  const setCardField = (field: keyof ContactListItem, v: string) => {
    const updatedEdits = {
      ...cardEdits,
      [selectedIdx]: { ...(cardEdits[selectedIdx] ?? {}), [field]: v },
    };
    setCardEdits(updatedEdits);
    const updatedLists = data.lists.map((item, i) => ({
      ...item,
      ...(updatedEdits[i] ?? {}),
    }));
    onChange({ ...data, lists: updatedLists });
  };

  const cardOptions = data.lists.map((l, i) => ({
    value: String(i),
    label: `Product / ${l.title}`,
  }));

  return (
    <>
      <LabelRow label="Main content title" required />
      <SplitRow currentText={`[ ${orig.current.label} ]`}>
        <DraftTextField placeholder="Main content title" onChange={v => set({ label: v })} />
      </SplitRow>

      <LabelRow label="Main title" />
      <SplitRow currentText={orig.current.title}>
        <DraftTextField placeholder="Main title" onChange={v => set({ title: v })} />
      </SplitRow>

      <LabelRow label="Main Description" />
      <SplitRow currentText={orig.current.description} multiline>
        <DraftTextArea placeholder="Main description" onChange={v => set({ description: v })} rows={3} />
      </SplitRow>

      <div className="scl--cms-content-edit-product-card-row">
        <span className="scl--cms-content-edit-product-label">Card / Contact us</span>
        <div className="scl--cms-content-edit-product-card-row-right">
          <SelectFieldInline
            className="scl--cms-content-edit-product-card-select"
            value={String(selectedIdx)}
            options={cardOptions}
            onChange={(v: string) => setSelectedIdx(Number(v))}
          />
        </div>
      </div>
      <div className="scl--cms-content-edit-product-divider" />

      {editedCard && (
        <>
          <LabelRow label="Card title" />
          <SplitRow currentText={`Title/ ${orig.current.lists[selectedIdx]?.title ?? ''}`}>
            <DraftTextField
              key={`title-${selectedIdx}`}
              initialValue={cardEdits[selectedIdx]?.title ?? ''}
              placeholder="Card title"
              onChange={v => setCardField('title', v)}
            />
          </SplitRow>

          <LabelRow label="Card description" />
          <SplitRow currentText={orig.current.lists[selectedIdx]?.description ?? ''} multiline>
            <DraftTextArea
              key={`desc-${selectedIdx}`}
              initialValue={cardEdits[selectedIdx]?.description ?? ''}
              placeholder="Card description"
              onChange={v => setCardField('description', v)}
              rows={3}
            />
          </SplitRow>

          <LabelRow label="Card button" />
          <SplitRow currentText={orig.current.lists[selectedIdx]?.buttonLabel ?? ''}>
            <DraftTextField
              key={`btn-${selectedIdx}`}
              initialValue={cardEdits[selectedIdx]?.buttonLabel ?? ''}
              placeholder="Card button name"
              onChange={v => setCardField('buttonLabel', v)}
            />
          </SplitRow>

          <ImageUploadField
            key={`contact-img-${selectedIdx}`}
            label="Icon"
            currentSrc={editedCard.imgIco || undefined}
            currentName={editedCard.imgIco ? orig.current.lists[selectedIdx]?.title : undefined}
            onFile={async f => {
              try {
                const formData = new FormData();
                formData.append('file',        f);
                formData.append('type',        'IMAGE');
                formData.append('filename',    f.name);
                formData.append('alt',         editedCard.imgAlt ?? '');
                formData.append('description', '');

                const res          = await ApiPost(`/api/v1/media/upload?userUuid=${userId}`, formData);
                const uploadedUrl: string | undefined  = (res as any)?.data?.data?.url;
                const uploadedUuid: string | undefined = (res as any)?.data?.data?.uuid;

                if (uploadedUrl) {
                  setCardField('imgIco', uploadedUrl);
                  if (uploadedUuid) registerMediaUsage(uploadedUuid);
                  return uploadedUrl;
                }
              } catch (err) {
                console.error('[ContactImgUpload] upload failed:', err);
              }
              return undefined;
            }}
            onMediaClick={
              onMediaClick
                ? () => onMediaClick(item => { if (item) setCardField('imgIco', item.src); })
                : undefined
            }
          />
        </>
      )}
    </>
  );
};

export default ContactEditor;